/**
 * @author Trendrating <info@trendrating.net>
 *
 * @module api/_StoredObjects
 * @summary Fixes the wrong server behaviour and provides an unified API to
 *      manage StoredObjects
 *
 * !!! IMPORTANT !!!
 * Classes that inherit _StoredObjects MUST define storedObjectType according to
 * supported server types
 *
 */

import { StoredObjectType } from "../types/Api";
import { deepClone } from "../deepClone";
import { endpoints } from "./endpoints";
import { _Base } from "./_Base";
export class _StoredObjects extends _Base {
  storedObjectType?: StoredObjectType;

  /**
   * Create a stored object
   *
   * @param {object} object
   * @param {string} object.name
   *
   * @returns {Promise} a promise fulfilled with the
   *       handled data of the response
   */
  async create(
    object: any,
    objectType?: StoredObjectType | null,
    user_id?: any
  ) {
    // const endPointRoot = this.getEndpointRoot(this.environment.api.compute);
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;

    const url = endPointRoot + endpoints.storedObjects.create;

    const _params = deepClone(object);
    if ("id" in _params) {
      delete _params.id;
    }

    // created/modified
    const timestamp = new Date().toISOString();
    _params.created = timestamp;
    _params.modified = timestamp;
    _params.ownerId = user_id;
    _params.type = objectType ? objectType : this.storedObjectType;

    const response = await this.preparePost(url, _params, null);
    return this._create(response);
  }

  async get(id?: any, objectType?: StoredObjectType, user_id?: any) {
    // const endPointRoot = this.getEndpointRoot(this.environment.api.compute);
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;

    let url: string = endPointRoot + endpoints.storedObjects.get;

    let params: any = {
      ownerId: user_id,
      type: objectType ? objectType : this.storedObjectType,
    };

    if (id != null) {
      url = endPointRoot + endpoints.storedObjects.getById;
      params = {
        id: id,
      };
    }

    const response = await this.prepareGet(url, params, null);
    return this._get(response);
  }

  async remove(object: any) {
    // const endPointRoot = this.getEndpointRoot(this.environment.api.compute);
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;

    const url = endPointRoot + endpoints.storedObjects.remove;

    if ("id" in object && object.id != null) {
      const queryParams = { id: object.id };
      const response = await this.prepareGet(url, queryParams, null);
      return this._remove(response);
    }
  }

  async update(
    object: any,
    objectType?: StoredObjectType | null,
    user_id?: any
  ) {
    // const endPointRoot = this.getEndpointRoot(this.environment.api.compute);
    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;

    const url = endPointRoot + endpoints.storedObjects.update;

    const _params = deepClone(object);

    // created/modified
    const timestamp = new Date().toISOString();
    _params["created"] = _params["created"] == null ? null : _params["created"]; // managing old without attribute
    _params["modified"] = timestamp;
    _params["ownerId"] = user_id;
    _params["type"] = objectType ? objectType : this.storedObjectType;

    const result = await this.preparePost(url, _params, null);
    return this._update(result);
  }



  async select(ownerIds:number[],objectType?: StoredObjectType) {

    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    let url: string = endPointRoot + endpoints.storedObjects.select;

    const payload = {
      searches: [
        {
          filters: [
            {
              dimension: "type",
              segments: [ objectType ? objectType : this.storedObjectType,
              ],
            },
            {
              dimension: "ownerId",
              segments: ownerIds,
            },
          ],
        },
      ],
    };

    try {
      const response = await this.preparePost(url, payload);
      return response?.data?.ids;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  async fetch(ids: number[], arrProps: string[], type?: StoredObjectType) {


    const endPointRoot = process.env.REACT_APP_ROOT_T_REST;
    const url = endPointRoot + endpoints.storedObjects.fetch;
    const payload = [
      {
        classType: type ? type : "PREFERENCE",
        id: ids,
        extendedRequest: {
          onDemandResult: arrProps.map((prop) => ({ dimension: prop })),
        },
      },
    ];

    const response = await this.preparePost(url, payload);
    let remappedDataObj: any = [];
    if (response) {
      for (const dataObj of response.data) {
        for (const row of dataObj.rows) {
          const remappedObj = {};

          for (const key in row) {
            if (key.startsWith("object.")) {
              const exploded = key.split(".");
              remappedObj[exploded[exploded.length - 1]] = row[key];
            } else {
              remappedObj[key] = row[key];
            }
          }

          remappedDataObj.push(remappedObj);
        }

        dataObj.rows = remappedDataObj;
      }
    }

    return response;
  }

  // ----------------------------------------------------- private methods
  _create(response: any) {
    const error = this.simulateHttpError(response);

    if (error != null) {
      return error;
    }

    return this._fix(response["data"]);
  }

  _get(response: any) {
    if ("rows" in response.data) {
      return response.data.rows.map((row: any) => this._fix(row));
    } else if ("preference" in response.data) {
      const error = this.simulateHttpError(response);

      if (error != null) {
        return error;
      }

      return this._fix(response.data.preference);
    }

    return [];
  }

  _fix(storedObject: any) {
    const _storedObject = deepClone(storedObject["object"]);
    _storedObject["id"] = storedObject["id"];
    _storedObject["ownerId"] = storedObject["ownerId"];

    if (storedObject["tags"] != null) {
      _storedObject["tags"] = storedObject["tags"];
    }

    return _storedObject;
  }

  _remove(response: any) {
    const error = this.simulateHttpError(response);

    if (error != null) {
      return error;
    }

    return { removed: true };
  }

  _update(response: any) {
    const error = this.simulateHttpError(response);

    if (error != null) {
      return error;
    }

    return this._fix(response["data"]);
  }
}
